/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Alert,
  AlertTitle,
  LinearProgress,
  Typography,
  Button,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import type {
  GridEventListener,
  GridPaginationModel,
  GridRowModel,
  GridRowModesModel,
} from '@mui/x-data-grid';
import {
  DataGrid,
  GridRowEditStopReasons,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import {
  useGetAllCompanyMasters,
  getActiveCompanyCount,
} from '../../../lib/query/companyMaster';
import { getLatestCompanyLimit } from '../../../lib/query/manageTenants';
import TenantContext from '../../../lib/context/TenantContext';
import { ValidateHeaders } from '../../../lib/services/importService';
import { getCurrentDate } from '../../../lib/utils/dateHelper';
import { uploadFileForImport } from '../../../lib/services/blob-service';
import { sendMessage } from '../../../lib/services/queue-service';
import { ImportType } from '../../../constants/importType';
import { updateEntity } from '../../../lib/services/api-service';
import { TableName } from '../../../constants/table';
import { columns } from './gridColumns';
import FileUpload from '../../../components/fileUpload';
import { AddComponentType } from '../../../constants/addComponentType';
import { ICompanyMaster } from '../../../models/companyMaster';
import { useMsal } from '@azure/msal-react';
import { usePusher } from '../../../lib/hooks/usePusher';
import { Link } from 'react-router-dom';
import GetAppIcon from '@mui/icons-material/GetApp';
import CompanyMappingsDialog from '../../../components/CompanyMappingsDialog';

const customClasses = {
  root: 'table-striped table-bordered',
  columnHeaderTitle: 'fw-bold',
  columnHeader: 'header',
  cell: 'cell',
  toolbarContainer: 'table-toolbar px-5',
  overlay: '',
  pagination: 'pagination',
  tooltip: 'tooltip',
  tooltipArrow: 'arrow',
};

const CompanyMaster = () => {
  const { tenant } = useContext(TenantContext);
  const { accounts } = useMsal();
  const {
    data: companyMasters,
    refetch,
    isLoading,
  } = useGetAllCompanyMasters(tenant.id ?? '');


  const [showErrorAlert, setShowErrorAlert] = React.useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // State for Company Mappings Dialog
  const [mappingsDialogOpen, setMappingsDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<{
    id: string;
    name: string;
    gst?: string;
  } | null>(null);

  const handleManageMappings = (
    companyId: string,
    companyName: string,
    companyGst: string,
  ) => {
    setSelectedCompany({ id: companyId, name: companyName, gst: companyGst });
    setMappingsDialogOpen(true);
  };

  const handleCloseMappingsDialog = () => {
    setMappingsDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleImportStarted = (data: any) => {
    toast.info(data.message);
    setIsProcessing(true);
  };

  const handleImportCompleted = (data: any) => {
    toast.success(data.message);
    setIsProcessing(false);
    refetch();
  };

  const handleImportFailed = (data: any) => {
    toast.error(data.message);
    setIsProcessing(false);
    refetch();
  };

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-started',
    callback: handleImportStarted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-completed',
    callback: handleImportCompleted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-failed',
    callback: handleImportFailed,
  });

  const showError = () => {
    setShowErrorAlert(true);
  };

  const handleClose = (reason: any) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowErrorAlert(false);
  };

  const headers = ['Company OCR', 'Company', 'Status'];

  const onFileUpload = (file: any, reset: () => void) => {
    ValidateHeaders(file, headers).then(res => {
      if (res) {
        setShowSuccessAlert(true);
        const currentBatchId = uuid();

        const filePath = `import/${tenant.id}/company-master/${getCurrentDate()}/${currentBatchId}/${file?.name}`;
        setIsProcessing(true);
        uploadFileForImport(file, filePath)
          .then(() => {
            sendMessage(
              'import-master',
              JSON.stringify({
                fileName: filePath,
                tenantId: tenant.id,
                importType: ImportType.CompanyMaster,
                batchId: currentBatchId,
                userId: accounts[0]?.idTokenClaims?.preferred_username,
              }),
            );
          })
          .then(() => reset());
      } else {
        showError();
      }
    });
  };

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer style={{ justifyContent: 'space-between' }}>
        <div>
          <GridToolbarQuickFilter />
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
        </div>
      </GridToolbarContainer>
    );
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const processRowUpdate = async (
    newRow: GridRowModel<ICompanyMaster>,
    oldRow: GridRowModel<ICompanyMaster>,
  ) => {
    try {
      const newRowActive = newRow.Active ?? false;
      const oldRowActive = oldRow.Active ?? false;
      if (
        newRow.CompanyName !== oldRow.CompanyName ||
        newRowActive !== oldRowActive
      ) {
        const latestCompanyLicenseLimit = await getLatestCompanyLimit(
          tenant.id,
        );

        const activeCompanyCount = await getActiveCompanyCount(tenant.id);

        if (
          newRowActive &&
          (latestCompanyLicenseLimit ?? 1) <= (activeCompanyCount ?? 0) &&
          !companyMasters
            ?.filter(e => e.Active)
            .some(e => e.CompanyName === newRow.CompanyName)
        ) {
          toast.warn(
            'Bill To company name limit exceeded, please contact help desk.',
          );
          return oldRow;
        }
      }

      const updatedRow = {
        ...newRow,
        isNew: false,
        IsSystemGenerated: false,
      };

      const entityData: Partial<ICompanyMaster> = {
        PartitionKey: newRow.PartitionKey,
        RowKey: oldRow.RowKey,
        Active: newRow.Active,
        CompanyName: newRow.CompanyName,
        ExportFormat: newRow.ExportFormat,
        IsSystemGenerated: false,
      };

      await updateEntity(TableName.CompanyMaster, entityData);

      refetch();
      toast.success('CompanyMaster info saved');
      return updatedRow;
    } catch (error) {
      toast.error('Failed to update CompanyMaster info');
    }
    return oldRow;
  };

  /* eslint no-param-reassign: ["error", { "props": false }] */
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  return (
    <div className="mt-4">
      <Typography variant="h4" sx={{ color: 'black', fontWeight: '600' }}>
        Company Master
      </Typography>

      <div className="flex py-4 items-center justify-between">
        <div className="font-semibold text-xl">Organization: {tenant.name}</div>
        <div className="flex items-center">
          <Button
            component={Link}
            to="/admin/tally-company-import"
            variant="contained"
            startIcon={<GetAppIcon />}
            sx={{ mr: 2 }}
            size="small">
            Import from Tally
          </Button>
          <FileUpload
            onFileUpload={onFileUpload}
            headers={headers}
            addComponentType={AddComponentType.CompanyMaster}
            showFileImport={false}
          />
        </div>
      </div>
      <div className="my-2">{isProcessing && <LinearProgress />}</div>

      <DataGrid
        rows={companyMasters ?? []}
        columns={columns(rowModesModel, setRowModesModel, handleManageMappings)}
        initialState={{
          columns: {
            columnVisibilityModel: {
              RowKey: false,
            },
          },
        }}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={(newRow, oldRow) => processRowUpdate(newRow, oldRow)}
        editMode="row"
        slots={{ toolbar: CustomToolbar }}
        autoHeight
        sx={{
          boxShadow: 2,
          borderColor: 'blueGrey',
          '& .MuiDataGrid-cell:hover': {
            color: 'primary.light',
          },
          '& .gridHeader--header': {
            color: '#000000',
            fontSize: 'large',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '700',
            color: '#020617',
          },
          '.row-red': {
            background: '#ff9100',
            color: '#000000',
          },
        }}
        loading={isLoading}
        classes={customClasses}
        pageSizeOptions={[5, 10, 20]}
        paginationModel={{ page, pageSize: rowsPerPage }}
        onPaginationModelChange={onPaginationModelChange}
        getRowClassName={params =>
          params.row.IsSystemGenerated ? 'row-red' : ''
        }
      />
      <Snackbar
        open={showErrorAlert}
        autoHideDuration={3000}
        onClose={(_event, reason) => handleClose(reason)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowErrorAlert(false)}
          severity="error"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION FAILED:</AlertTitle>
          FILE FORMAT DID NOT MATCH
        </Alert>
      </Snackbar>
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={1500}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowErrorAlert(false)}
          severity="success"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION PASSED:</AlertTitle>
          UPLOADING FILE FOR PROCESSING.
        </Alert>
      </Snackbar>

      {/* Company Mappings Dialog */}
      {selectedCompany && selectedCompany.id && (
        <CompanyMappingsDialog
          open={mappingsDialogOpen}
          onClose={handleCloseMappingsDialog}
          companyId={selectedCompany.id}
          companyName={selectedCompany.name}
          tenantId={tenant.id ?? ''}
          onMappingsChanged={refetch}
        />
      )}
    </div>
  );
};
export default CompanyMaster;
